import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Button,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getEventDetail,
  getEventGroups,
  updateEventDetails,
  updateEventAgenda,
  updateEventHosts,
  deleteEvent,
  rsvpEvent,
} from '../../../endpoints/eventsTs';
import {
  EventDetailResponse,
  UpdateEventDetailsRequest,
  AgendaItemInput,
  HostRequest,
  HostResponse,
  RSVPRequest,
} from '../../../types/events';
import { eventType } from '../../../utils/variables';
import AlertItem from '../../../Components/AlertItem';
import UserSearchModal from '../../../Components/UserSearchModal';
import StateSelector from '../../../Components/StateSelector';
import CountrySelector from '../../../Components/CountrySelector';
import { getCountries, getStates } from '../../../endpoints/lookups';

dayjs.extend(utc);

type EditingSection = null | 'details' | 'agenda' | 'hosts';

// Local state for details form
interface DetailsFormState {
  title: string;
  description: string;
  meeting_on: string;
  ending_on: string;
  meeting_link: string;
  event_type_id: string;
  group_id: string;
  location_name: string;
  location_user_id: string;
  meeting_address: {
    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    state_id?: string;
    postal_code?: string;
    country_id?: string;
  };
}

// Local state for agenda item (no id)
interface AgendaEditItem {
  title: string;
  description: string;
  order: number;
}

// Local state for host in edit (user_id + display name)
interface HostEditItem {
  user_id: string;
  name: string;
  is_primary: boolean;
}

const defaultDetailsForm: DetailsFormState = {
  title: '',
  description: '',
  meeting_on: '',
  ending_on: '',
  meeting_link: '',
  event_type_id: '',
  group_id: '',
  location_name: '',
  location_user_id: '',
  meeting_address: {},
};

const convertUTCToLocal = (
  utcDateTimeString: string | null | undefined
): string => {
  if (!utcDateTimeString) return '';
  return dayjs.utc(utcDateTimeString).local().format('YYYY-MM-DD HH:mm:ss');
};

const convertToUTCISO = (dateTimeString: string): string => {
  if (!dateTimeString) return dateTimeString;
  return dayjs(dateTimeString).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
};

const formatEventDate = (dateString: string): string => {
  if (!dateString) return '';
  return dayjs.utc(dateString).local().format('MMM D, YYYY h:mm A');
};

const isEventInFuture = (event: EventDetailResponse | null): boolean => {
  if (!event) return false;
  return dayjs(event.meeting_on).isAfter(dayjs());
};

const EventDetailPage: React.FC = () => {
  const { id: chapterId, eventId } = useParams<{
    id: string;
    eventId: string;
  }>();
  const navigate = useNavigate();

  const [editingSection, setEditingSection] = useState<EditingSection>(null);
  const [detailsForm, setDetailsForm] =
    useState<DetailsFormState>(defaultDetailsForm);
  const [detailsLocationType, setDetailsLocationType] = useState<
    'member' | 'business'
  >('member');
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [agendaEditItems, setAgendaEditItems] = useState<AgendaEditItem[]>([]);
  const [agendaError, setAgendaError] = useState<string | null>(null);
  const [hostEditItems, setHostEditItems] = useState<HostEditItem[]>([]);
  const [hostError, setHostError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showHostModal, setShowHostModal] = useState(false);
  const [showLocationUserModal, setShowLocationUserModal] = useState(false);
  const [rsvpAttending, setRsvpAttending] = useState(true);
  const [rsvpNotes, setRsvpNotes] = useState('');
  const [rsvpSubmitting, setRsvpSubmitting] = useState(false);
  const [rsvpError, setRsvpError] = useState<string | null>(null);

  // Country/state for details form
  const [detailsSelectedCountry, setDetailsSelectedCountry] =
    useState<any>(null);
  const [detailsSelectedState, setDetailsSelectedState] = useState<any>(null);

  const {
    data: event,
    isFetching,
    refetch,
  } = useApiGet(['event-detail', eventId], () => getEventDetail(eventId!), {
    enabled: !!eventId,
    retry: 1,
  });

  const { data: eventGroups } = useApiGet(
    ['event-groups', chapterId],
    () => getEventGroups(chapterId!),
    {
      enabled: !!chapterId,
      retry: 1,
    }
  );

  const { data: countries } = useApiGet(['donation-countries'], getCountries, {
    enabled: true,
  });
  const countryIdForStates =
    detailsSelectedCountry?.id || detailsForm.meeting_address?.country_id;
  const { data: states } = useApiGet(
    ['donation-states', countryIdForStates],
    () => getStates({ countryId: countryIdForStates }),
    { enabled: !!countryIdForStates }
  );

  const eventTypes = useMemo(
    () => [
      { id: eventType.Meeting, name: 'Meeting' },
      { id: eventType.Social, name: 'Social' },
    ],
    []
  );

  // Populate details form when entering edit mode
  useEffect(() => {
    if (editingSection === 'details' && event) {
      setDetailsLocationType(event.location_user_id ? 'member' : 'business');
      setDetailsForm({
        title: event.title,
        description: event.description || '',
        meeting_on: convertUTCToLocal(event.meeting_on),
        ending_on: convertUTCToLocal(event.ending_on || ''),
        meeting_link: event.meeting_link || '',
        event_type_id: event.event_type_id,
        group_id: event.group_id || '',
        location_name: event.location_name || '',
        location_user_id: event.location_user_id || '',
        meeting_address: event.meeting_address
          ? {
              address_line_1: event.meeting_address.address_line_1 || '',
              address_line_2: event.meeting_address.address_line_2 || '',
              city: event.meeting_address.city || '',
              state_id: event.meeting_address.state_id || '',
              postal_code: event.meeting_address.postal_code || '',
              country_id: event.meeting_address.country_id || '',
            }
          : {},
      });
      setDetailsError(null);
    }
  }, [editingSection, event]);

  // Populate agenda when entering edit mode
  useEffect(() => {
    if (editingSection === 'agenda' && event?.agenda) {
      const sorted = [...event.agenda].sort((a, b) => a.order - b.order);
      setAgendaEditItems(
        sorted.map((a) => ({
          title: a.title,
          description: a.description || '',
          order: a.order,
        }))
      );
      setAgendaError(null);
    }
  }, [editingSection, event?.agenda]);

  // Populate hosts when entering edit mode (use user_id from API)
  useEffect(() => {
    if (editingSection === 'hosts' && event?.hosts) {
      setHostEditItems(
        event.hosts.map((h: HostResponse) => ({
          user_id: h.user_id || h.id,
          name: h.name,
          is_primary: h.is_primary,
        }))
      );
      setHostError(null);
    }
  }, [editingSection, event?.hosts]);

  // Sync RSVP form when event loads
  useEffect(() => {
    if (event) {
      setRsvpAttending(event.attending);
    }
  }, [event]);

  // Populate country/state when details form has address
  useEffect(() => {
    if (
      editingSection === 'details' &&
      detailsForm.meeting_address?.country_id &&
      countries &&
      !detailsSelectedCountry
    ) {
      const found = countries.find(
        (c: any) => c.id === detailsForm.meeting_address?.country_id
      );
      if (found) setDetailsSelectedCountry(found);
    }
  }, [
    countries,
    editingSection,
    detailsForm.meeting_address?.country_id,
    detailsSelectedCountry,
  ]);

  useEffect(() => {
    if (
      editingSection === 'details' &&
      detailsForm.meeting_address?.state_id &&
      states &&
      !detailsSelectedState
    ) {
      const found = states.find(
        (s: any) => s.id === detailsForm.meeting_address?.state_id
      );
      if (found) setDetailsSelectedState(found);
    }
  }, [
    states,
    editingSection,
    detailsForm.meeting_address?.state_id,
    detailsSelectedState,
  ]);

  const buildDetailsPayload = (): UpdateEventDetailsRequest => {
    const payload: UpdateEventDetailsRequest = {
      title: detailsForm.title,
      description: detailsForm.description || null,
      meeting_on: convertToUTCISO(detailsForm.meeting_on),
      ending_on: convertToUTCISO(detailsForm.ending_on),
      meeting_link: detailsForm.meeting_link || null,
      event_type_id: detailsForm.event_type_id,
      group_id: detailsForm.group_id || null,
    };
    if (detailsLocationType === 'member') {
      payload.location_user_id = detailsForm.location_user_id || null;
      payload.location_name = null;
      payload.meeting_address = null;
    } else {
      payload.location_user_id = null;
      payload.location_name = detailsForm.location_name || null;
      payload.meeting_address = detailsForm.meeting_address?.address_line_1
        ? {
            address_line_1: detailsForm.meeting_address.address_line_1,
            address_line_2:
              detailsForm.meeting_address.address_line_2 || undefined,
            city: detailsForm.meeting_address.city || undefined,
            state_id: detailsForm.meeting_address.state_id || undefined,
            postal_code: detailsForm.meeting_address.postal_code || undefined,
            country_id: detailsForm.meeting_address.country_id || undefined,
          }
        : null;
    }
    return payload;
  };

  const { mutate: saveDetailsMutation } = useApiSend(
    (data: UpdateEventDetailsRequest) => updateEventDetails(eventId!, data),
    () => {
      setEditingSection(null);
      refetch();
    },
    (err: Error) => {
      setDetailsError(err?.message || 'Failed to save details');
    },
    [['event-detail', eventId]]
  );

  const { mutate: saveAgendaMutation } = useApiSend(
    (agenda: AgendaItemInput[]) => updateEventAgenda(eventId!, { agenda }),
    () => {
      setEditingSection(null);
      refetch();
    },
    (err: Error) => {
      setAgendaError(err?.message || 'Failed to save agenda');
    },
    [['event-detail', eventId]]
  );

  const { mutate: saveHostsMutation } = useApiSend(
    (hosts: HostRequest[]) => updateEventHosts(eventId!, { hosts }),
    () => {
      setEditingSection(null);
      refetch();
    },
    (err: Error) => {
      setHostError(err?.message || 'Failed to save hosts');
    },
    [['event-detail', eventId]]
  );

  const { mutate: deleteEventMutation } = useApiSend(
    () => deleteEvent(eventId!),
    () => {
      setShowDeleteConfirm(false);
      navigate(`/local-chapters/${chapterId}?tab=events`, { replace: true });
    },
    (err: Error) => console.error(err),
    [['lc-events', chapterId]]
  );

  const handleSaveDetails = () => {
    setDetailsError(null);
    if (
      !detailsForm.title ||
      !detailsForm.meeting_on ||
      !detailsForm.event_type_id
    ) {
      setDetailsError(
        'Please fill in title, meeting date/time, and event type.'
      );
      return;
    }
    if (!detailsForm.ending_on) {
      setDetailsError('Ending date/time is required.');
      return;
    }
    if (dayjs(detailsForm.ending_on).isBefore(dayjs(detailsForm.meeting_on))) {
      setDetailsError('Ending date/time cannot be before meeting date/time.');
      return;
    }
    saveDetailsMutation(buildDetailsPayload());
  };

  const handleSaveAgenda = () => {
    setAgendaError(null);
    const payload = agendaEditItems.map((a, i) => ({
      title: a.title,
      description: a.description || null,
      order: i,
    }));
    saveAgendaMutation(payload);
  };

  const handleSaveHosts = () => {
    setHostError(null);
    const hosts: HostRequest[] = hostEditItems.map((h) => ({
      user_id: h.user_id,
      is_primary: h.is_primary,
    }));
    saveHostsMutation(hosts);
  };

  const handleAddAgendaItem = () => {
    setAgendaEditItems((prev) => [
      ...prev,
      { title: '', description: '', order: prev.length },
    ]);
  };

  const handleRemoveAgendaItem = (index: number) => {
    setAgendaEditItems((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.map((a, i) => ({ ...a, order: i }));
    });
  };

  const handleAgendaItemChange = (
    index: number,
    field: 'title' | 'description',
    value: string
  ) => {
    setAgendaEditItems((prev) =>
      prev.map((a, i) => (i === index ? { ...a, [field]: value } : a))
    );
  };

  const handleAddHost = () => {
    setShowHostModal(true);
  };

  const handleHostUserSelected = (user: any) => {
    const name = user.full_name || user.name || `User ${user.id}`;
    const isPrimary = hostEditItems.length === 0;
    setHostEditItems((prev) => [
      ...prev,
      { user_id: user.id, name, is_primary: isPrimary },
    ]);
    setShowHostModal(false);
  };

  const handleRemoveHost = (index: number) => {
    setHostEditItems((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length && next.every((h) => !h.is_primary)) {
        next[0].is_primary = true;
      }
      return next;
    });
  };

  const handleSetPrimaryHost = (index: number) => {
    setHostEditItems((prev) =>
      prev.map((h, i) => ({ ...h, is_primary: i === index }))
    );
  };

  const handleDetailsLocationTypeChange = (type: 'member' | 'business') => {
    setDetailsLocationType(type);
    if (type === 'member') {
      setDetailsForm((prev) => ({
        ...prev,
        location_name: '',
        meeting_address: {},
      }));
      setDetailsSelectedCountry(null);
      setDetailsSelectedState(null);
    } else {
      setDetailsForm((prev) => ({ ...prev, location_user_id: '' }));
    }
  };

  const handleDetailsCountryChange = (country: any) => {
    setDetailsSelectedCountry(country);
    setDetailsSelectedState(null);
    setDetailsForm((prev) => ({
      ...prev,
      meeting_address: {
        ...prev.meeting_address,
        country_id: country?.id || '',
        state_id: '',
      },
    }));
  };

  const handleDetailsStateChange = (state: any) => {
    setDetailsSelectedState(state);
    setDetailsForm((prev) => ({
      ...prev,
      meeting_address: {
        ...prev.meeting_address,
        state_id: state?.id || '',
      },
    }));
  };

  const handleLocationUserSelected = (user: any) => {
    setDetailsForm((prev) => ({ ...prev, location_user_id: user.id }));
    setShowLocationUserModal(false);
  };

  const handleRsvpSubmit = () => {
    if (!eventId) return;
    setRsvpError(null);
    setRsvpSubmitting(true);
    const userId = localStorage.getItem('user_id');
    const payload: RSVPRequest = {
      is_attending: rsvpAttending,
      user_id: userId || undefined,
      notes: rsvpNotes || undefined,
    };
    rsvpEvent(eventId, payload)
      .then(() => {
        refetch();
        setRsvpSubmitting(false);
      })
      .catch((err) => {
        setRsvpError(err?.message || 'Failed to save RSVP');
        setRsvpSubmitting(false);
      });
  };

  if (!eventId || !chapterId) {
    return (
      <Box p={2}>
        <Typography>Invalid route. Missing chapter or event.</Typography>
      </Box>
    );
  }

  if (isFetching && !event) {
    return (
      <Box p={2}>
        <Typography>Loading event...</Typography>
      </Box>
    );
  }

  if (!event) {
    return (
      <Box p={2}>
        <Typography>Event not found.</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/local-chapters/${chapterId}/events`)}
        >
          Back to events
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 800, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/local-chapters/${chapterId}/events`)}
        sx={{ mb: 2 }}
      >
        Back to events
      </Button>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5">{event.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {formatEventDate(event.meeting_on)}
            {event.ending_on && ` – ${formatEventDate(event.ending_on)}`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Chip label={event.event_type_name} size="small" />
            {event.group_name && (
              <Chip label={event.group_name} size="small" variant="outlined" />
            )}
            {event.attending && (
              <Chip label="Attending" color="success" size="small" />
            )}
          </Box>
        </Box>
        {event.can_edit && isEventInFuture(event) && (
          <Button
            startIcon={<DeleteIcon />}
            color="error"
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete
          </Button>
        )}
      </Box>

      {/* Event Details card */}
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title="Event Details"
          action={
            event.can_edit &&
            editingSection !== 'details' && (
              <Button
                startIcon={<EditIcon />}
                size="small"
                onClick={() => setEditingSection('details')}
              >
                Edit
              </Button>
            )
          }
        />
        <CardContent>
          {editingSection === 'details' ? (
            <Grid container spacing={2}>
              {detailsError && (
                <Grid item xs={12}>
                  <Box sx={{ mb: 2, width: '100%' }}>
                    <AlertItem
                      severity="error"
                      description={detailsError}
                      onClose={() => setDetailsError(null)}
                      keepOpen={true}
                    />
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  label="Event Title"
                  value={detailsForm.title}
                  onChange={(e) =>
                    setDetailsForm((p) => ({ ...p, title: e.target.value }))
                  }
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={detailsForm.description}
                  onChange={(e) =>
                    setDetailsForm((p) => ({
                      ...p,
                      description: e.target.value,
                    }))
                  }
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Meeting Date/Time"
                  value={
                    detailsForm.meeting_on
                      ? dayjs(detailsForm.meeting_on)
                      : null
                  }
                  onChange={(v) =>
                    setDetailsForm((p) => ({
                      ...p,
                      meeting_on: v ? v.format('YYYY-MM-DD HH:mm:ss') : '',
                    }))
                  }
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Ending Date/Time"
                  value={
                    detailsForm.ending_on ? dayjs(detailsForm.ending_on) : null
                  }
                  onChange={(v) =>
                    setDetailsForm((p) => ({
                      ...p,
                      ending_on: v ? v.format('YYYY-MM-DD HH:mm:ss') : '',
                    }))
                  }
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Event Type</InputLabel>
                  <Select
                    value={detailsForm.event_type_id}
                    label="Event Type"
                    onChange={(e) =>
                      setDetailsForm((p) => ({
                        ...p,
                        event_type_id: e.target.value,
                      }))
                    }
                  >
                    {eventTypes.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Group (Optional)</InputLabel>
                  <Select
                    value={detailsForm.group_id}
                    label="Group"
                    onChange={(e) =>
                      setDetailsForm((p) => ({
                        ...p,
                        group_id: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">None</MenuItem>
                    {eventGroups?.map((g) => (
                      <MenuItem key={g.id} value={g.id}>
                        {g.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Meeting Link"
                  value={detailsForm.meeting_link}
                  onChange={(e) =>
                    setDetailsForm((p) => ({
                      ...p,
                      meeting_link: e.target.value,
                    }))
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <FormLabel component="legend">Location</FormLabel>
                <RadioGroup
                  row
                  value={detailsLocationType}
                  onChange={(e) =>
                    handleDetailsLocationTypeChange(
                      e.target.value as 'member' | 'business'
                    )
                  }
                >
                  <FormControlLabel
                    value="member"
                    control={<Radio />}
                    label="Member's Residence"
                  />
                  <FormControlLabel
                    value="business"
                    control={<Radio />}
                    label="Other"
                  />
                </RadioGroup>
              </Grid>
              {detailsLocationType === 'member' && (
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={() => setShowLocationUserModal(true)}
                    fullWidth
                  >
                    {detailsForm.location_user_id
                      ? 'Change Location Member'
                      : 'Select Location Member'}
                  </Button>
                  <UserSearchModal
                    modalOpen={showLocationUserModal}
                    setModalOpen={setShowLocationUserModal}
                    chapter={chapterId}
                    spd={undefined}
                    userObjectRet={true}
                    setUser={handleLocationUserSelected}
                    statusFilter="active"
                  />
                </Grid>
              )}
              {detailsLocationType === 'business' && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      label="Location Name"
                      value={detailsForm.location_name}
                      onChange={(e) =>
                        setDetailsForm((p) => ({
                          ...p,
                          location_name: e.target.value,
                        }))
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Address Line 1"
                      value={detailsForm.meeting_address?.address_line_1 || ''}
                      onChange={(e) =>
                        setDetailsForm((p) => ({
                          ...p,
                          meeting_address: {
                            ...p.meeting_address,
                            address_line_1: e.target.value,
                          },
                        }))
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Address Line 2 (Optional)"
                      value={detailsForm.meeting_address?.address_line_2 || ''}
                      onChange={(e) =>
                        setDetailsForm((p) => ({
                          ...p,
                          meeting_address: {
                            ...p.meeting_address,
                            address_line_2: e.target.value,
                          },
                        }))
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="City"
                      value={detailsForm.meeting_address?.city || ''}
                      onChange={(e) =>
                        setDetailsForm((p) => ({
                          ...p,
                          meeting_address: {
                            ...p.meeting_address,
                            city: e.target.value,
                          },
                        }))
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CountrySelector
                      value={detailsForm.meeting_address?.country_id || ''}
                      handleChange={handleDetailsCountryChange}
                      required={false}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <StateSelector
                      value={detailsForm.meeting_address?.state_id || ''}
                      handleChange={handleDetailsStateChange}
                      countryId={detailsForm.meeting_address?.country_id}
                      required={false}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Postal Code"
                      value={detailsForm.meeting_address?.postal_code || ''}
                      onChange={(e) =>
                        setDetailsForm((p) => ({
                          ...p,
                          meeting_address: {
                            ...p.meeting_address,
                            postal_code: e.target.value,
                          },
                        }))
                      }
                      fullWidth
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Button variant="contained" onClick={handleSaveDetails}>
                  Save
                </Button>
                <Button sx={{ ml: 1 }} onClick={() => setEditingSection(null)}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          ) : (
            <>
              {event.description && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {event.description}
                </Typography>
              )}
              {(event.meeting_link ||
                event.meeting_address ||
                event.location_name) && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Location
                  </Typography>
                  {event.meeting_link && (
                    <Typography variant="body2">
                      <a
                        href={event.meeting_link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {event.meeting_link}
                      </a>
                    </Typography>
                  )}
                  {event.location_name && (
                    <Typography variant="body2">
                      {event.location_name}
                    </Typography>
                  )}
                  {event.meeting_address && (
                    <Typography variant="body2">
                      {event.meeting_address.address_line_1}
                      {event.meeting_address.address_line_2 &&
                        `, ${event.meeting_address.address_line_2}`}
                      <br />
                      {event.meeting_address.city}
                      {event.meeting_address.state &&
                        `, ${event.meeting_address.state}`}
                      {event.meeting_address.postal_code &&
                        ` ${event.meeting_address.postal_code}`}
                    </Typography>
                  )}
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Agenda card */}
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title="Agenda"
          action={
            event.can_edit &&
            editingSection !== 'agenda' && (
              <Button
                startIcon={<EditIcon />}
                size="small"
                onClick={() => setEditingSection('agenda')}
              >
                Edit
              </Button>
            )
          }
        />
        <CardContent>
          {editingSection === 'agenda' ? (
            <>
              {agendaError && (
                <Box sx={{ mb: 2, width: '100%' }}>
                  <AlertItem
                    severity="error"
                    description={agendaError}
                    onClose={() => setAgendaError(null)}
                    keepOpen={true}
                  />
                </Box>
              )}
              {agendaEditItems.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <IconButton size="small" sx={{ mt: 1 }} disabled>
                    <DragIndicatorIcon />
                  </IconButton>
                  <Grid container spacing={1} sx={{ flex: 1 }}>
                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        placeholder="Title"
                        value={item.title}
                        onChange={(e) =>
                          handleAgendaItemChange(index, 'title', e.target.value)
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        placeholder="Description (optional)"
                        value={item.description}
                        onChange={(e) =>
                          handleAgendaItemChange(
                            index,
                            'description',
                            e.target.value
                          )
                        }
                        fullWidth
                        multiline
                      />
                    </Grid>
                  </Grid>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveAgendaItem(index)}
                    sx={{ mt: 1 }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddAgendaItem}
                sx={{ mt: 1 }}
              >
                Add item
              </Button>
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" onClick={handleSaveAgenda}>
                  Save
                </Button>
                <Button sx={{ ml: 1 }} onClick={() => setEditingSection(null)}>
                  Cancel
                </Button>
              </Box>
            </>
          ) : (
            <>
              {event.agenda && event.agenda.length > 0 ? (
                <List dense>
                  {[...event.agenda]
                    .sort((a, b) => a.order - b.order)
                    .map((item) => (
                      <ListItem key={item.id}>
                        <ListItemText
                          primary={item.title}
                          secondary={item.description}
                        />
                      </ListItem>
                    ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No agenda items.
                </Typography>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Hosts card */}
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title="Hosts"
          action={
            event.can_edit &&
            editingSection !== 'hosts' && (
              <Button
                startIcon={<EditIcon />}
                size="small"
                onClick={() => setEditingSection('hosts')}
              >
                Edit
              </Button>
            )
          }
        />
        <CardContent>
          {editingSection === 'hosts' ? (
            <>
              {hostError && (
                <Box sx={{ mb: 2, width: '100%' }}>
                  <AlertItem
                    severity="error"
                    description={hostError}
                    onClose={() => setHostError(null)}
                    keepOpen={true}
                  />
                </Box>
              )}
              <List dense>
                {hostEditItems.map((h, index) => (
                  <ListItem
                    key={h.user_id}
                    secondaryAction={
                      <Box>
                        <Button
                          size="small"
                          onClick={() => handleSetPrimaryHost(index)}
                          disabled={h.is_primary}
                        >
                          {h.is_primary ? 'Primary' : 'Set primary'}
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveHost(index)}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={h.name}
                      secondary={h.is_primary ? 'Primary' : ''}
                    />
                  </ListItem>
                ))}
              </List>
              <Button startIcon={<AddIcon />} onClick={handleAddHost}>
                Add host
              </Button>
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" onClick={handleSaveHosts}>
                  Save
                </Button>
                <Button sx={{ ml: 1 }} onClick={() => setEditingSection(null)}>
                  Cancel
                </Button>
              </Box>
              <UserSearchModal
                modalOpen={showHostModal}
                setModalOpen={setShowHostModal}
                chapter={chapterId}
                spd={undefined}
                userObjectRet={true}
                setUser={handleHostUserSelected}
                statusFilter="active"
              />
            </>
          ) : (
            <>
              {event.hosts && event.hosts.length > 0 ? (
                <Typography variant="body2">
                  {event.hosts
                    .map((h) => h.name + (h.is_primary ? ' (Primary)' : ''))
                    .join(', ')}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No hosts.
                </Typography>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* RSVP */}
      {isEventInFuture(event) && (
        <Card sx={{ mb: 2 }}>
          <CardHeader title="Your RSVP" />
          <CardContent>
            {rsvpError && (
              <Box sx={{ mb: 2, width: '100%' }}>
                <AlertItem
                  severity="error"
                  description={rsvpError}
                  onClose={() => setRsvpError(null)}
                  keepOpen={true}
                />
              </Box>
            )}
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend">Attendance</FormLabel>
              <RadioGroup
                row
                value={rsvpAttending ? 'yes' : 'no'}
                onChange={(e) => setRsvpAttending(e.target.value === 'yes')}
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio />}
                  label="Attending"
                />
                <FormControlLabel
                  value="no"
                  control={<Radio />}
                  label="Not attending"
                />
              </RadioGroup>
            </FormControl>
            <TextField
              label="Notes (optional)"
              value={rsvpNotes}
              onChange={(e) => setRsvpNotes(e.target.value)}
              fullWidth
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              onClick={handleRsvpSubmit}
              disabled={rsvpSubmitting}
            >
              {rsvpSubmitting ? 'Saving...' : 'Save RSVP'}
            </Button>
          </CardContent>
        </Card>
      )}

      <Typography variant="body2" color="text.secondary">
        RSVPs: {event.rsvp_count}
      </Typography>

      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <DialogTitle>Delete event?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete this event. This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteEventMutation(undefined)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventDetailPage;
